import { useCallback, useEffect, useRef, useState } from "react";
import {
  createSavedQuery,
  deleteSavedQuery,
  listSavedQueries,
} from "./queryBuilderApi";

/**
 * Owns the company's saved-query list plus create/delete actions. Fetches are
 * race-guarded so a stale response can never overwrite a newer list.
 * @returns {object} saved-query state and actions.
 */
export function useSavedQueries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fetchIdRef = useRef(0);

  const refresh = useCallback(() => {
    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);
    listSavedQueries().then((res) => {
      if (fetchId !== fetchIdRef.current) return;
      if (res.isSuccess && Array.isArray(res.data)) {
        setItems(res.data);
      } else {
        setError(res.message || "Failed to load saved queries");
      }
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /**
   * Saves a new query, then refreshes the list.
   * @param {string} name the query name.
   * @param {object} config the query config to persist.
   * @returns {Promise<{ok: boolean, message?: string}>}
   */
  const save = useCallback(
    async (name, config) => {
      const res = await createSavedQuery(name, config);
      if (res.isSuccess) refresh();
      return { ok: !!res.isSuccess, message: res.message };
    },
    [refresh],
  );

  /**
   * Deletes a saved query, then refreshes the list.
   * @param {number} id the saved-query id.
   * @returns {Promise<{ok: boolean, message?: string}>}
   */
  const remove = useCallback(
    async (id) => {
      const res = await deleteSavedQuery(id);
      if (res.isSuccess) refresh();
      return { ok: !!res.isSuccess, message: res.message };
    },
    [refresh],
  );

  return { items, loading, error, refresh, save, remove };
}
